import React, { FC, useMemo } from 'react';
import { EmptyState } from '../empty_state/EmptyState';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { PARTS_EQUIPMENTS_WORK_ORDERS_CRUD_ACTION_CREATORS } from '../../actions/parts_equipments_work_orders_actions';
import { partsEquipmentsWorkOrdersRestCrudThunksForSupplier } from '../../thunks/parts_equipments_work_orders_thunks';
import PartsWithQuantityRowDisplay from './PartsWithQuantityRowDisplay';
import { isSupplierAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { nullSafeGet, renderCurrency } from '../../utils/DataAccessUtils';
import { FINISHED_WORK_ORDER_DISPLAY_STATUSES } from '../../constants/WorkOrderDisplayStatuses';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';

const TC_NAME = 'workOrderAddedPartsIndex';

const WorkOrderPartsSection: FC<any> = ({
	showQuantity,
	workOrder,
	editPartQuantity,
	onRemovePart,

	updateFilters,
	clearAndUpdateFilters,
	workOrdersPartsEquipment,
	companyConfig,
	currentUser,
	match,
}): React.ReactElement => {
	const workOrderId = useMemo(() => match.params.id, [match.params.id]);

	const canViewCost = useMemo(
		() =>
			isSupplierAllowedToAccess(
				PERMISSION_NAMES.CAN_VIEW_PART_EQUIPMENT_COST_IN_WO,
				companyConfig,
				nullSafeGet('roles', currentUser)
			),
		[companyConfig, currentUser]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Part',
				dataIndex: 'partId',
				render: (text, record) => (
					<PartsWithQuantityRowDisplay workOrderPart={record} workOrder={workOrder} />
				),
			},
			{
				title: 'Quantity',
				dataIndex: ['partsQuantity'],
				render: (qty) =>
					qty ? (
						qty
					) : (
						<div className="flex flex-row items-center">
							<div>0</div>
							<div className="ml-1 text-gray-400">{`(Returned)`}</div>
						</div>
					),
			},
			...(canViewCost
				? [
						{
							title: 'Cost',
							dataIndex: 'totalPartsCost',
							render: (cost) => renderCurrency(currentUser)(cost),
						},
				  ]
				: []),
			{
				render: (_, record) =>
					FINISHED_WORK_ORDER_DISPLAY_STATUSES.includes(workOrder.displayStatus) ? null : (
						<div className="flex flex-row items-center">
							<EditOutlined
								title="Edit"
								className="hover:cursor-pointer"
								onClick={editPartQuantity(record)}
							/>
							<div className="ml-2">
								<Popconfirm
									onConfirm={onRemovePart(record)}
									title="Are you sure you want to remove this part?"
									okText="Yes"
									cancelText="No"
								>
									<DeleteOutlined title="Delete" style={{ marginLeft: 8 }} />
								</Popconfirm>
							</div>
						</div>
					),
			},
		],
		[canViewCost, currentUser, editPartQuantity, onRemovePart, workOrder]
	);

	return (
		<PaginatedReduxTable
			targetCollectionName={TC_NAME}
			updateFilters={updateFilters}
			stateSlice={workOrdersPartsEquipment}
			clearAndUpdateFilters={clearAndUpdateFilters}
			filterConfig={[]}
			collectionName="work_orders_parts_equipment"
			columns={columns}
			fetchData={partsEquipmentsWorkOrdersRestCrudThunksForSupplier.read}
			rightActions={null}
			showHeader={showQuantity}
			hideFilters
			initialFilters={{ workOrderId, isEquipmentLine: false }}
			emptyState={
				<EmptyState
					graphic={
						<img
							alt="No Data"
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
						/>
					}
					headline={'No Parts Added'}
					body={null}
				/>
			}
		/>
	);
};

const mapStateToProps = (state) => ({
	workOrdersPartsEquipment: state.work_orders_parts_equipment,
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(
			PARTS_EQUIPMENTS_WORK_ORDERS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			PARTS_EQUIPMENTS_WORK_ORDERS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(WorkOrderPartsSection)
);
